import ChevronLeftIcon from "@atlaskit/icon/core/chevron-left";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import ProjectStatusIcon from "@atlaskit/icon/core/project-status";
import {token} from "@atlaskit/tokens";
import {useState, useSyncExternalStore, type ReactNode} from "react";
import {GenericModal} from "~/components/genericModal";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import {
    DESKTOP_DEFAULT_CALENDAR_VIEW_MODE,
    getCalendarToolbarHandlers,
    getCalendarToolbarLegendItems,
    getCalendarToolbarTitle,
    getCalendarToolbarViewMode,
    subscribeCalendarToolbar,
    type CalendarViewMode,
} from "~/components/beatPlanningCalendar/calendarToolbarBus";
import "./beatPlanningCalendar.css";

const CALENDAR_VIEW_LABELS: Record<CalendarViewMode, string> = {
    timeGridDay: "Day",
    timeGridFourDay: "4 Days",
    timeGridWeek: "Week",
    dayGridMonth: "Month",
    scheduleList: "List",
    multiMonthYear: "Year",
};

const LEGEND_DESCRIPTION_BY_LABEL: Record<string, string> = {
    Draft: "Event is in draft status",
    Live: "Event is currently active",
    Scheduled: "Event is scheduled for the future",
    Completed: "Event has been completed",
    Cancelled: "Event has been cancelled",
    Deleted: "Event has been deleted",
};

export function CalendarToolbarActions({
    isMobile,
    trailingContent,
}: {
    isMobile: boolean;
    trailingContent?: ReactNode;
}) {
    const handlers = useSyncExternalStore(subscribeCalendarToolbar, getCalendarToolbarHandlers);
    const title = useSyncExternalStore(subscribeCalendarToolbar, getCalendarToolbarTitle);
    const currentViewMode = useSyncExternalStore(subscribeCalendarToolbar, getCalendarToolbarViewMode);
    const legendItems = useSyncExternalStore(subscribeCalendarToolbar, getCalendarToolbarLegendItems);
    const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
    if (handlers === null) {
        return null;
    }

    return (
        <div className="beat-planning-calendar__toolbar-right">
            <AnankeButton
                type="button"
                appearance="default"
                spacing="default"
                iconBefore={(iconProps) => <ProjectStatusIcon {...iconProps} label="" />}
                onClick={() => setIsLegendModalOpen(true)}
            >
                Legend
            </AnankeButton>

            <AnankeButton
                type="button"
                appearance="default"
                spacing="default"
                onClick={handlers.today}
            >
                Today
            </AnankeButton>

            {isMobile === false ? (
                <div className="beat-planning-calendar__month-nav">
                    <div className="beat-planning-calendar__month-nav-group">
                        <AnankeButton
                            type="button"
                            appearance="subtle"
                            spacing="default"
                            className="beat-planning-calendar__toolbar-icon-button"
                            iconBefore={(iconProps) => <ChevronLeftIcon {...iconProps} label="" />}
                            onClick={handlers.prev}
                        />
                        <span className="beat-planning-calendar__month-title">
                            {title}
                        </span>
                        <AnankeButton
                            type="button"
                            appearance="subtle"
                            spacing="default"
                            className="beat-planning-calendar__toolbar-icon-button"
                            iconBefore={(iconProps) => <ChevronRightIcon {...iconProps} label="" />}
                            onClick={handlers.next}
                        />
                    </div>
                </div>
            ) : null}

            <AnankeDropdownMenu<HTMLButtonElement>
                placement="bottom-end"
                trigger={({triggerRef, ...triggerProps}) => (
                    <AnankeButton
                        {...triggerProps}
                        ref={triggerRef}
                        type="button"
                        appearance="default"
                        spacing="default"
                        iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} label="" />}
                    >
                        {CALENDAR_VIEW_LABELS[currentViewMode] ??
                            CALENDAR_VIEW_LABELS[DESKTOP_DEFAULT_CALENDAR_VIEW_MODE]}
                    </AnankeButton>
                )}
            >
                <AnankeDropdownItemGroup>
                    <AnankeDropdownItem onClick={() => handlers.setView("timeGridDay")}>
                        Day
                    </AnankeDropdownItem>
                    <AnankeDropdownItem onClick={() => handlers.setView("timeGridFourDay")}>
                        4 Days
                    </AnankeDropdownItem>
                    <AnankeDropdownItem onClick={() => handlers.setView("timeGridWeek")}>
                        Week
                    </AnankeDropdownItem>
                    <AnankeDropdownItem onClick={() => handlers.setView("dayGridMonth")}>
                        Month
                    </AnankeDropdownItem>
                    <AnankeDropdownItem onClick={() => handlers.setView("scheduleList")}>
                        List
                    </AnankeDropdownItem>
                    <AnankeDropdownItem onClick={() => handlers.setView("multiMonthYear")}>
                        Year
                    </AnankeDropdownItem>
                </AnankeDropdownItemGroup>
            </AnankeDropdownMenu>
            {trailingContent}
            <GenericModal
                opened={isLegendModalOpen}
                onClose={() => setIsLegendModalOpen(false)}
                title="Event status legend"
                size="xl"
            >
                <div className="beat-planning-calendar__legend-modal">
                    <div className="beat-planning-calendar__legend-modal-subtitle">
                        Color coding for event statuses in the calendar view
                    </div>
                    <div className="beat-planning-calendar__legend-modal-grid">
                        {legendItems.map((item) => {
                            const strokeColor = item.color?.borderColor ?? item.color?.textColor ?? token("color.border");
                            const blockColor = item.color?.backgroundColor ?? token("color.icon.inverse");
                            const description =
                                item.description ?? LEGEND_DESCRIPTION_BY_LABEL[item.label] ?? "Event status";
                            return (
                                <div
                                    key={item.type}
                                    className="beat-planning-calendar__legend-modal-item"
                                >
                                    <div
                                        className="beat-planning-calendar__legend-modal-color-block"
                                        style={{
                                            backgroundColor: blockColor,
                                        }}
                                    >
                                        <div
                                            className="beat-planning-calendar__legend-modal-color-stroke"
                                            style={{backgroundColor: strokeColor}}
                                        />
                                    </div>
                                    <div className="beat-planning-calendar__legend-modal-text">
                                        <div className="beat-planning-calendar__legend-modal-label">{item.label}</div>
                                        {isMobile === false ? (
                                            <div className="beat-planning-calendar__legend-modal-description">
                                                {description}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </GenericModal>
        </div>
    );
}
